/**
 * Reponer desde la ficha: las existencias de cada variante, en una tabla.
 *
 * Con doce combinaciones, reponer «M / Azul» obligaba a abrir el asistente
 * entero. Aquí sólo se cambian existencias —opciones, precios y SKU siguen en
 * el asistente— y se guarda de una vez con «Guardar», que sólo aparece cuando
 * hay algo cambiado. Vacío = sin límite, como en el asistente.
 *
 * Las que se acaban van marcadas («Agotado», «Quedan 2»), que es justo lo que
 * se viene a buscar cuando la campana avisa.
 */
import { useEffect, useMemo, useState } from 'react'
import { IconPackage } from '@tabler/icons-react'
import { setOfferVariantStock, type Offer } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'
import { OfferCard } from './OfferCard'

/** Desde cuántas se marca «quedan pocas»: el mismo umbral que la campana. */
const LOW_AT = 3

const textOf = (stock: number | null) => (stock === null ? '' : String(stock))

export function VariantStockCard({ projectId, token, offer, onChange }: {
  projectId: string
  token: string
  offer: Offer
  onChange: (offer: Offer) => void
}) {
  const { t } = useI18n()
  const variants = useMemo(() => (offer.variants ?? []).filter((variant) => variant.active), [offer.variants])
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  /* Lo guardado manda otra vez cuando la oferta cambia (otra pestaña, el asistente). */
  useEffect(() => { setDraft({}) }, [offer.variants])

  if (!variants.length) return null
  const valueOf = (id: string, stock: number | null) => draft[id] ?? textOf(stock)
  const changed = variants.filter((variant) => draft[variant.id] !== undefined && draft[variant.id] !== textOf(variant.stock))

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      onChange(await setOfferVariantStock(projectId, offer.id, changed.map((variant) => {
        const raw = (draft[variant.id] ?? '').trim()
        return { id: variant.id, stock: raw === '' ? null : Number.parseInt(raw, 10) }
      }), token))
      setDraft({})
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t('offers.variants.stockSaveFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <OfferCard
      className="dash-vstock-card"
      icon={<IconPackage size={15} />}
      title={t('offers.variants.stockCard')}
      count={variants.length}
      action={changed.length ? (
        <span className="dash-vstock-actions">
          <button type="button" className="dash-secondary-btn" disabled={busy} onClick={() => setDraft({})}>{t('common.cancel')}</button>
          <button type="button" className="dash-primary-btn" disabled={busy} onClick={() => { void save() }}>
            {busy ? t('common.saving') : t('common.save')}
          </button>
        </span>
      ) : null}
    >
      <div className="dash-vstock" role="table" aria-label={t('offers.variants.stockCard')}>
        {variants.map((variant) => {
          const value = valueOf(variant.id, variant.stock)
          const left = value.trim() === '' ? null : Number.parseInt(value, 10)
          return (
            <div key={variant.id} role="row" className={`dash-vstock-row${left === 0 ? ' is-out' : left !== null && left <= LOW_AT ? ' is-low' : ''}`}>
              <span role="cell" className="dash-vstock-name">
                <strong title={variant.label}>{variant.label}</strong>
                {variant.sku ? <small>{variant.sku}</small> : null}
              </span>
              <span role="cell" className="dash-vstock-tag">
                {left === 0 ? t('offers.detail.soldOut') : left !== null && left <= LOW_AT ? t('offers.variants.fewLeft', { count: left }) : ''}
              </span>
              <span role="cell" className="dash-vstock-input">
                <input
                  inputMode="numeric"
                  value={value}
                  placeholder="∞"
                  title={value.trim() === '' ? t('offers.variants.unlimited') : undefined}
                  aria-label={t('offers.variants.stockAria', { variant: variant.label })}
                  disabled={busy}
                  onChange={(event) => {
                    const next = event.target.value.replace(/[^\d]/g, '')
                    setDraft((current) => ({ ...current, [variant.id]: next }))
                  }}
                  onKeyDown={(event) => { if (event.key === 'Enter' && changed.length) { event.preventDefault(); void save() } }}
                />
              </span>
            </div>
          )
        })}
      </div>
      {error ? <p className="dash-svc-field-error" role="alert">{error}</p> : <p className="dash-vx-hint">{t('offers.variants.stockCardHint')}</p>}
    </OfferCard>
  )
}
